import { useCallback, useMemo, useState } from 'react';
import { FilterCard, MetricCard, MetricGrid, SectionCard } from '@/components/DataList';
import { ListTable, ScreenFrame } from '@/components/Screen';
import { Icon } from '@/components/icons';
import { admin } from '@/api/endpoints';
import { pickList, type JsonMap } from '@/api/client';
import { useAsync } from '@/hooks/useAsync';
import {
  Button,
  Empty,
  ErrorBox,
  Input,
  Loading,
  Money,
  ProSearch,
  Select,
  StatusBadge,
} from '@/components/ui';
import { formatDateTime, str } from '@/utils/format';

const SOURCE_OPTIONS = [
  { value: 'all', label: 'كل المصادر' },
  { value: 'wallet', label: 'محافظ المستخدمين' },
  { value: 'deposits', label: 'طلبات الشحن' },
  { value: 'orders', label: 'طلبات الوحدات' },
  { value: 'games', label: 'طلبات الألعاب' },
  { value: 'tahweel', label: 'عمليات التحويل' },
  { value: 'purchases', label: 'مشتريات الرصيد' },
] as const;

function monthStart(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function directionLabel(dir: unknown): string {
  const d = str(dir, '').toLowerCase();
  if (d === 'credit') return 'داخل';
  if (d === 'debit') return 'خارج';
  if (d === 'pending') return 'معلّق';
  if (d === 'neutral') return 'معلومات';
  return d || '—';
}

function moneyForRow(row: JsonMap) {
  const cur = str(row.currency, 'SYP').toUpperCase();
  const suffix = cur === 'USD' ? '$' : undefined;
  return <Money value={row.amount ?? row.cost} suffix={suffix} />;
}

export function FinancialScreen() {
  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState(todayIso);
  const [source, setSource] = useState('all');
  const [q, setQ] = useState('');
  const [applied, setApplied] = useState({ from: monthStart(), to: todayIso(), source: 'all', q: '' });

  const load = useCallback(async () => {
    const summaryParams = { from: applied.from, to: applied.to };
    const ledgerParams = {
      from: applied.from,
      to: applied.to,
      source: applied.source,
      q: applied.q,
    };
    const [summary, ledger] = await Promise.all([
      admin.financialSummary(summaryParams),
      admin.financialLedger(ledgerParams),
    ]);
    if (!summary.success) return summary;
    const summaryData =
      summary.data && typeof summary.data === 'object' ? (summary.data as JsonMap) : (summary as JsonMap);
    const ledgerRows = ledger.success
      ? pickList(ledger, 'data', 'entries', 'items', 'ledger')
      : [];
    return { success: true as const, summary: summaryData, ledger: ledgerRows };
  }, [applied]);

  const state = useAsync(
    load,
    (p) => ({
      summary: (p.summary as JsonMap) ?? {},
      ledger: Array.isArray(p.ledger) ? (p.ledger as JsonMap[]) : [],
    }),
    [applied],
  );

  function applyFilters() {
    setApplied({ from, to, source, q: q.trim() });
  }

  const totals = useMemo(() => {
    const s = state.data?.summary ?? {};
    const t = s.totals && typeof s.totals === 'object' ? (s.totals as JsonMap) : s;
    return t;
  }, [state.data?.summary]);

  const ledger = state.data?.ledger ?? [];

  if (state.loading && !state.data) return <Loading />;
  if (state.error) return <ErrorBox message={state.error} onRetry={state.reload} />;

  return (
    <ScreenFrame
      mode="admin"
      icon="chart"
      title="التقارير المالية"
      subtitle="تقرير شامل لحركات المحافظ، الشحن، الطلبات، المبيعات، الديون، والأرباح."
      actions={
        <Button type="button" onClick={() => state.reload()}>
          <Icon name="refresh" size={18} /> تحديث التقرير
        </Button>
      }
    >
      <FilterCard
        title="تصفية التقرير"
        icon={<Icon name="settings" size={24} />}
        subtitle="حدد الفترة والمصدر لعرض الحركات المالية بالتفصيل."
      >
        <Input
          label="من تاريخ"
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
        />
        <Input
          label="إلى تاريخ"
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />
        <Select
          label="المصدر"
          value={source}
          onChange={setSource}
          options={[...SOURCE_OPTIONS]}
        />
        <ProSearch value={q} onChange={setQ} placeholder="بحث باسم، رقم، وصف، طلب..." />
        <Button type="button" onClick={applyFilters} disabled={state.loading}>
          {state.loading ? 'جاري التحميل...' : 'تطبيق التصفية'}
        </Button>
      </FilterCard>

      <MetricGrid>
        <MetricCard
          title="دخل الليرة"
          value={<Money value={totals.syp_in || 0} />}
          hint="كل عمليات credit بالليرة"
          icon={<Icon name="deposit" size={21} />}
        />
        <MetricCard
          title="خرج الليرة"
          value={<Money value={totals.syp_out || 0} />}
          hint="كل عمليات debit بالليرة"
          icon={<Icon name="wallet" size={21} />}
        />
        <MetricCard
          title="صافي الليرة"
          value={<Money value={totals.syp_net || 0} />}
          hint="دخل - خرج"
          icon={<Icon name="chart" size={21} />}
        />
        <MetricCard
          title="طلبات معلقة"
          value={`${str(totals.pending_deposits_syp || 0)} / ${str(totals.pending_deposits_usd || 0)}$`}
          hint="طلبات شحن بانتظار مراجعة"
          icon={<Icon name="bell" size={21} />}
        />
      </MetricGrid>

      <MetricGrid>
        <MetricCard
          title="دخل الدولار"
          value={<Money value={totals.usd_in || 0} suffix="$" />}
          hint="محفظة الألعاب"
          icon={<Icon name="game" size={21} />}
        />
        <MetricCard
          title="خرج الدولار"
          value={<Money value={totals.usd_out || 0} suffix="$" />}
          hint="طلبات وأرصدة الألعاب"
          icon={<Icon name="wallet" size={21} />}
        />
        <MetricCard
          title="ربح الألعاب"
          value={<Money value={totals.games_profit || 0} suffix="$" />}
          hint={`مبيعات ${str(totals.games_sales || 0)} - تكلفة ${str(totals.games_cost || 0)}`}
          icon={<Icon name="game" size={21} />}
        />
        <MetricCard
          title="أرصدة المستخدمين"
          value={`${str(totals.users_recharge_balance || 0)} / ${str(totals.users_games_balance || 0)}$`}
          hint="التزامك الحالي تجاه العملاء"
          icon={<Icon name="users" size={21} />}
        />
      </MetricGrid>

      <MetricGrid>
        <MetricCard
          title="مبيعات الوحدات"
          value={<Money value={totals.tahweel_sales || 0} />}
          hint={`تكلفة ${str(totals.tahweel_cost || 0)}`}
          icon={<Icon name="deposit" size={21} />}
        />
        <MetricCard
          title="ربح الوحدات"
          value={<Money value={totals.tahweel_profit || 0} />}
          hint="حسب عمليات التحويل الناجحة"
          icon={<Icon name="check" size={21} />}
        />
        <MetricCard
          title="مشتريات الرصيد"
          value={<Money value={totals.purchases_total || 0} />}
          hint="رصيد/كاش اشتريته من الموردين"
          icon={<Icon name="package" size={21} />}
        />
        <MetricCard
          title="ديون مفتوحة"
          value={<Money value={totals.open_debts || 0} />}
          hint="مبالغ لم تُحصّل بعد"
          icon={<Icon name="bill" size={21} />}
        />
      </MetricGrid>

      <SectionCard
        title={`دفتر الحركات التفصيلي (${ledger.length})`}
        subtitle="سجل تفصيلي للحركات المالية والتشغيلية."
        icon={<Icon name="orders" size={24} />}
      >
        {ledger.length === 0 ? (
          <Empty title="لا توجد حركات" hint="غيّر الفترة أو المصدر ثم طبّق التصفية." />
        ) : (
          <ListTable
            headers={['التاريخ', 'المصدر', 'الوصف', 'المستخدم/الجهة', 'الاتجاه', 'المبلغ', 'تكلفة/ربح', 'الحالة']}
            tableClassName="finance-table"
          >
            <tbody>
              {ledger.map((e, i) => (
                <tr key={str(e.id, String(i))}>
                  <td>{formatDateTime(e.created_at)}</td>
                  <td>{str(e.source_label ?? e.source, '—')}</td>
                  <td>{str(e.title ?? e.description, '—')}</td>
                  <td>
                    <div className="cell-stack">
                      <strong>{str(e.user_name, '—')}</strong>
                      <small dir="ltr">{str(e.user_phone, '')}</small>
                    </div>
                  </td>
                  <td>{directionLabel(e.direction)}</td>
                  <td>{moneyForRow(e)}</td>
                  <td>
                    {e.profit !== undefined || e.cost !== undefined ? (
                      <span>
                        {e.cost !== undefined ? <Money value={e.cost} /> : null}
                        {e.profit !== undefined ? (
                          <>
                            {' / '}
                            <Money value={e.profit} suffix={str(e.currency) === 'USD' ? '$' : undefined} />
                          </>
                        ) : null}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>
                    <StatusBadge status={e.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </ListTable>
        )}
      </SectionCard>
    </ScreenFrame>
  );
}
